/* eslint-disable @typescript-eslint/no-explicit-any */
import {
 Autocomplete,
 Box,
 Button,
 Chip,
 FormControl,
 InputLabel,
 MenuItem,
 Select,
 TextField,
 Typography,
} from '@mui/material';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UserTabProps } from './types';
import UserAvatar from '@components/common/Header/UserBadge';
import TwoFactorSection from './TwoFactorSection';
import SelectApi from '../SelectApi';
import { Theme } from '@mui/material/styles';

type SendMethod = { Id: number; Name: string };
const sendMethodsList: SendMethod[] = [
 { Id: 1, Name: 'SMS' },
 { Id: 2, Name: 'Email' },
];

const UserTab: React.FC<UserTabProps> = ({
 control,
 errors,
 self,
 groups,
 loading,
 setValue,
}) => {
 const { t } = useTranslation();
 const isDisabled = loading.usersUpdateSelf || loading.usersGetSelf || false;

 return (
  <form id="user-profile-form">
   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
    <UserAvatar isHorizontal={true} />

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
     <Typography
      sx={{
       fontSize: '14px',
       color: (th) => th.palette.kemek.darkGray,
       fontWeight: 500,
      }}
     >
      {t('user.settings')}
     </Typography>
     <Box sx={{ display: 'flex', gap: 2 }}>
      <Controller
       name="Name"
       control={control}
       disabled={isDisabled}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth
         label={t('user.fields.name')}
         variant="outlined"
         error={!!errors.Name}
         helperText={errors.Name?.message}
        />
       )}
      />
      <Controller
       name="Surname"
       control={control}
       disabled={isDisabled}
       render={({ field }) => (
        <TextField
         {...field}
         fullWidth
         label={t('user.fields.surname')}
         variant="outlined"
         error={!!errors.Surname}
         helperText={errors.Surname?.message}
        />
       )}
      />
     </Box>
     <FormControl fullWidth error={!!errors.Customer}>
      <Controller
       name="Customer"
       control={control}
       disabled={true}
       render={({ field }) => {
        return (
         <SelectApi
          {...field}
          isEditable
          operator="contains"
          url="/api/Customers/v2/"
          filter={`{"fields":"Id,Name"}`}
          label={t('user.fields.representing')}
          control={control}
          setValue={setValue}
         />
        );
       }}
      />
     </FormControl>
     <FormControl fullWidth error={!!errors.UserGroups}>
      <SelectApi
       isEditable
       disabled={isDisabled}
       name="UserGroups"
       operator="eq"
       url="/api/Users/v1/groups"
       filter='{"fields":"Id,Name"}'
       label={t('user.fields.group')}
       multiple
       control={control}
       setValue={setValue}
      />
     </FormControl>
     <FormControl fullWidth error={!!errors.SendMethods}>
      <Controller
       name="SendMethods"
       control={control}
       disabled={isDisabled}
       render={({ field }) => (
        <Autocomplete<SendMethod, true>
         {...field}
         multiple
         options={sendMethodsList}
         getOptionLabel={(option) => option.Name}
         isOptionEqualToValue={(option, val) => option.Id === val.Id}
         value={
          field.value
           ?.map((id) => sendMethodsList.find((m) => m.Id === id))
           .filter((item): item is SendMethod => !!item) || []
         }
         onChange={(_, value) => field.onChange(value.map((item) => item.Id))}
         renderTags={(value, getTagProps) =>
          value.map((option, index) => (
           <Chip
            {...getTagProps({ index })}
            key={option.Id}
            label={option.Name}
            onDelete={() => {
             const newValue = field.value?.filter(
              (id: number) => id !== option.Id,
             );
             field.onChange(newValue);
            }}
           />
          ))
         }
         renderInput={(params) => (
          <TextField
           {...params}
           label={t('user.fields.notificationMethod')}
           variant="outlined"
          />
         )}
        />
       )}
      />
     </FormControl>
     <Controller
      name="Phone"
      control={control}
      render={({ field }) => (
       <TextField
        {...field}
        fullWidth
        label={t('user.fields.phone')}
        type="tel"
        variant="outlined"
        error={!!errors.Phone}
        helperText={errors.Phone?.message}
        disabled={isDisabled}
       />
      )}
     />
    </Box>
   </Box>
  </form>
 );
};

export default UserTab;
